import { DatePipe } from '@angular/common';
import {
  afterNextRender,
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { AvatarPillComponent, IconPillComponent, ImageUrlPipe, InsightMarkdownPipe } from '@frontend/shared';
import { Image } from '@frontend/core';
import { MediaService } from '../../media';
import { Culture } from '../../../cultures';
import {
  INSIGHT_TEXT_FIELDS,
  INSIGHT_TEXT_FIELD_LABELS,
  InsightTextField,
} from '../../../contributions/insight-base.model';
import { InsightExtended } from '../../../contributions/insight-extended.model';
import { SUPPORTED_LOCALES, SupportedLocale } from '../../../language/locale.model';
import { TranslocoDirective } from "@jsverse/transloco";
import { Difference } from '../../../differences';

/** One field's translation coverage: every UI locale, flagged present or missing. */
export interface InsightTranslationCoverageRow {
  field: InsightTextField;
  label: string;
  locales: { locale: SupportedLocale; exists: boolean }[];
  /** False when the field holds no locale at all — i.e. there is nothing to translate. */
  hasAnyLocale: boolean;
}


@Component({
  selector: 'multisite-insights-viewer-card',
  imports: [
    DatePipe,
    AvatarPillComponent,
    IconPillComponent,
    ImageUrlPipe,
    InsightMarkdownPipe,
    TranslocoDirective
],
  templateUrl: './insights-viewer-card.component.html',
  styleUrls: ['./insights-viewer-card.component.scss'],
})
export class InsightsViewerCardComponent {
  insight = input.required<InsightExtended>();
  title = input<string>('');
  isBodyExpanded = input<boolean>(false);
  bodyExpandedToggle = output<boolean>();
  showId = input<boolean>(false);
  showSlug = input<boolean>(false);
  showCategory = input<boolean>(false);
  showType = input<boolean>(false);
  showCultures = input<boolean>(false);
  showDifferences = input<boolean>(false);
  showTopics = input<boolean>(false);
  /**
   * Show which locales each text field already holds. Off by default: it is editorial
   * information, and this card is also used for ordinary end users, who should not see it.
   */
  showAvailableTranslations = input<boolean>(false);
  /**
   * Offer the "send for translation" button alongside the coverage badges. Separate from
   * {@link showAvailableTranslations} because reading coverage is harmless while sending
   * costs money and (unless the caller runs a dry run) mutates the insight. The card only
   * emits {@link sendForTranslationRequest} — the caller owns the request and its options.
   */
  showSendForTranslation = input<boolean>(false);
  sendForTranslationRequest = output<void>();

  /** Index into {@link images}; the thumbnail sits at 0 so it shows by default. */
  private readonly currentImageIndex = signal(0);

  /**
   * Collapsed height of the body, in px. Fed to the template as a CSS custom property so
   * the SCSS clamp and this measurement stay in sync from a single source of truth.
   */
  readonly collapsedBodyMaxHeightPx = 160;

  /** The body element, measured to decide whether collapsing is actually needed. */
  private readonly bodyRef = viewChild<ElementRef<HTMLElement>>('bodyEl');

  /** True once we've measured the body at least once (avoids a flash before we know). */
  private readonly hasMeasuredBody = signal(false);

  /** Whether the body's natural height exceeds the collapsed height, i.e. content is clipped. */
  private readonly isBodyOverflowing = signal(false);

  /** Bumped by the ResizeObserver so the measuring effect re-runs on width-driven reflow. */
  private readonly reflowTick = signal(0);

  private readonly destroyRef = inject(DestroyRef);

  constructor(private mediaService: MediaService) {
    // Measure the real rendered height after every render that can affect it. `scrollHeight`
    // reports the full content height even while the element is clamped, so this is accurate
    // in both collapsed and expanded states — no character counting, works with arbitrary HTML.
    afterRenderEffect({
      read: () => {
        // Track everything that changes the body's rendered height.
        this.getInsightBodyMarkdown();
        this.currentImage();
        this.isBodyExpanded();
        this.reflowTick();
        const el = this.bodyRef()?.nativeElement;
        if (!el) {
          return;
        }
        this.isBodyOverflowing.set(el.scrollHeight > this.collapsedBodyMaxHeightPx + 1);
        this.hasMeasuredBody.set(true);
      },
    });

    // Catch container/width changes (responsive reflow) that no signal reflects, and re-measure.
    afterNextRender(() => {
      const el = this.bodyRef()?.nativeElement;
      if (!el || typeof ResizeObserver === 'undefined') {
        return;
      }
      const observer = new ResizeObserver(() => this.reflowTick.update((tick) => tick + 1));
      observer.observe(el);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  readonly displayTitle = computed(() => this.title() || this.insight().s || 'Untitled insight');

  /**
   * Translation coverage per field. `translated_locales` arrays are unordered sets, so this
   * walks the UI locales in a fixed order and tests membership rather than reading positions.
   */
  readonly translationCoverage = computed<InsightTranslationCoverageRow[]>(() => {
    const translatedLocales = this.insight()?.translated_locales;

    return INSIGHT_TEXT_FIELDS.map((field) => {
      const localesForField = translatedLocales?.[field] ?? [];

      return {
        field,
        label: INSIGHT_TEXT_FIELD_LABELS[field],
        locales: SUPPORTED_LOCALES.map((locale) => ({ locale, exists: localesForField.includes(locale) })),
        hasAnyLocale: localesForField.length > 0,
      };
    });
  });

  requestSendForTranslation(): void {
    this.sendForTranslationRequest.emit();
  }

  /**
   * Whether the body is long enough that collapsing adds value. Assumed true until the first
   * measurement lands so short content isn't briefly clipped, then driven by the real height.
   */
  readonly isBodyCollapsible = computed(
    () => !this.hasMeasuredBody() || this.isBodyOverflowing()
  );

  /** Actual collapsed visual state: collapsible content that the user hasn't expanded. */
  readonly isBodyCollapsed = computed(() => !this.isBodyExpanded() && this.isBodyCollapsible());

  /** Only offer the toggle once we know for certain the content overflows. */
  readonly showBodyToggle = computed(() => this.hasMeasuredBody() && this.isBodyOverflowing());

  /**
   * The insight's images with the `thumbnail`-role image moved to the front, so it is
   * the one shown by default. Order is otherwise preserved. Empty when there are none.
   */
  readonly images = computed<Image[]>(() => {
    const images = this.insight()?.images ?? [];
    const thumbnailIndex = images.findIndex((image) => image.role === 'thumbnail');
    if (thumbnailIndex <= 0) {
      return images;
    }
    const ordered = [...images];
    const [thumbnail] = ordered.splice(thumbnailIndex, 1);
    return [thumbnail, ...ordered];
  });

  readonly hasMultipleImages = computed(() => this.images().length > 1);

  /** Human-facing 1-based position of the current image, for the "n / total" counter. */
  readonly currentImagePosition = computed(() => {
    const count = this.images().length;
    return count ? (this.currentImageIndex() % count) + 1 : 0;
  });

  readonly currentImage = computed<Image | null>(() => {
    const images = this.images();
    if (!images.length) {
      return null;
    }
    return images[this.currentImageIndex() % images.length] ?? null;
  });

  requestNextImage(): void {
    const count = this.images().length;
    if (count < 2) {
      return;
    }
    this.currentImageIndex.update((index) => (index + 1) % count);
  }

  requestBodyExpandedToggle(): void {
    this.bodyExpandedToggle.emit(!this.isBodyExpanded());
  }

  /**
   * The body as it is **stored**: canonical GFM Markdown in CKEditor's dialect,
   * not HTML. The template renders it through the `insightMarkdown` pipe, which
   * converts and sanitises it; this returns the source so the measuring effect
   * above has something to track and so the conversion stays in one place.
   */
  getInsightBodyMarkdown(): string {
    const insight = this.insight();
    return insight?.l || insight?.m || '';
  }

  getDifferenceNames(): string {
    const insight = this.insight();
    return (insight.differences ?? [])
      .map((item: Difference) => item?.name || item?.slug)
      .filter(Boolean)
      .join(' • ');
  }

  getCultureFlagUrl(culture: Culture): string {
    return this.mediaService.getFlagUrlFromHash(culture?.flag?.hash, 'w_32,c_fill,ar_1:1,r_max/', true, '.png');
  }
}
